import { api } from './api'
import { dexieOperatorRosterRepo } from '../offline/repos/DexieOperatorRosterRepo'
import { ensureOfflineReady } from '../offline/initOffline'
import { verifyOperatorPinHash } from './operatorPinCrypto'

// Crew Mode Charter D-4 shared identity mechanism. Ties together the
// offline-cached roster (DexieOperatorRosterRepo), the API fetch, and PIN
// verification into the two operations a Crew Mode PIN screen needs:
// refresh the cache when online, and check an entered PIN against it —
// entirely offline once the cache is populated.

const isOnline = (): boolean => {
  if (typeof navigator === 'undefined') return true
  return navigator.onLine
}

/**
 * Fetches the current operator roster from the API and replaces the local
 * cache. No-ops (and does not throw) when offline — the existing cache
 * keeps serving PIN verification until the next successful refresh. Safe
 * to call opportunistically (app start, after an admin sets/clears a PIN,
 * periodically) rather than on a strict schedule.
 */
export const refreshOperatorRoster = async (): Promise<boolean> => {
  if (!isOnline()) return false

  await ensureOfflineReady()

  try {
    const entries = await api.teamMembers.operatorRoster()
    await dexieOperatorRosterRepo.replaceAll(entries.map((entry) => ({
      tenantMembershipId: entry.tenantMembershipId,
      actorId: entry.actorId,
      displayName: entry.displayName,
      pinHash: entry.pinHash,
      pinUpdatedAt: entry.pinUpdatedAt
    })))
    return true
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[operatorRoster] refresh failed, existing cache retained', error)
    }
    return false
  }
}

export type MatchedOperator = {
  tenantMembershipId: string
  actorId: string
  displayName: string
}

/**
 * Checks a candidate PIN against every cached roster entry and returns the
 * first match, or null. Entirely offline — never calls the API. A PIN
 * collision across operators (two people sharing the same 4-digit PIN) is
 * expected to be rare given a farm's operator count, and this returns
 * whichever entry matches first; if that becomes a real problem in the
 * pilot, the fix is enforcing PIN uniqueness at set-time (Stage 5 finding),
 * not here.
 */
export const findOperatorByPin = async (pin: string): Promise<MatchedOperator | null> => {
  await ensureOfflineReady()
  const roster = await dexieOperatorRosterRepo.list()

  for (const entry of roster) {
    // eslint-disable-next-line no-await-in-loop
    const matches = await verifyOperatorPinHash(pin, entry.pinHash)
    if (matches) {
      return {
        tenantMembershipId: entry.tenantMembershipId,
        actorId: entry.actorId,
        displayName: entry.displayName
      }
    }
  }

  return null
}
